import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import EventCard from "@/components/feature/EventCard";
import { specials } from "@/mocks/specials";
import { getSpecialEvents } from "@/utils/events";
import { setPageSeo } from "@/utils/seo";

const brandSuffix = "51每日大赛今日黑聚集地详情";

export default function Special() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get("topic") || specials[0].id;

  const activeTopic = useMemo(
    () => specials.find((topic) => topic.id === activeId) || specials[0],
    [activeId]
  );
  const topicEvents = useMemo(() => getSpecialEvents(activeTopic), [activeTopic]);
  const otherTopics = specials.filter((topic) => topic.id !== activeTopic.id);

  useEffect(() => {
    setPageSeo({
      title: `${activeTopic.title}专题 - ${brandSuffix}`,
      description: activeTopic.intro,
      keywords: `${activeTopic.title},赛事专题,赛事合集,${brandSuffix}`,
    });
  }, [activeTopic]);

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6">
      <nav className="flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">赛事专题</span>
      </nav>

      <header className="relative mt-5 overflow-hidden rounded-lg">
        <div className="relative h-56 w-full md:h-72">
          <img
            src={activeTopic.cover}
            alt={`${activeTopic.title} 专题封面`}
            title={`${activeTopic.title} 赛事专题`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/60 to-foreground-950/30"></div>
        </div>
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
          <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
            <i className="ri-bookmark-3-line"></i>
            赛事专题
          </span>
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
            {activeTopic.title}
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">
            {activeTopic.intro}
          </p>
        </div>
      </header>

      <div className="mt-8 flex flex-wrap gap-2">
        {specials.map((topic) => (
          <button
            key={topic.id}
            type="button"
            onClick={() => setSearchParams({ topic: topic.id })}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              topic.id === activeTopic.id
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {topic.title}
          </button>
        ))}
      </div>

      <section className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-6 md:p-8">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
          <i className="ri-lightbulb-line text-accent-600"></i>
          推荐理由
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
          {activeTopic.reason}
        </p>
      </section>

      <section className="mt-10">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-6 w-1 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              专题赛事列表
            </h2>
          </div>
          <span className="text-sm text-foreground-500">共 {topicEvents.length} 场赛事</span>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {topicEvents.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      </section>

      <section className="mt-16">
        <div className="mb-6 flex items-center gap-3">
          <span className="h-6 w-1 rounded-full bg-accent-500"></span>
          <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            相关专题
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {otherTopics.map((topic) => (
            <button
              key={topic.id}
              type="button"
              onClick={() => setSearchParams({ topic: topic.id })}
              className="group flex items-stretch gap-4 rounded-lg border border-background-200 bg-background-50 p-4 text-left transition-colors hover:border-primary-300"
            >
              <div className="h-24 w-32 shrink-0 overflow-hidden rounded-md bg-background-200">
                <img
                  src={topic.cover}
                  alt={`${topic.title} 专题封面`}
                  title={`${topic.title} 赛事专题`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col">
                <h3 className="font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                  {topic.title}
                </h3>
                <p className="mt-2 line-clamp-2 flex-1 text-sm text-foreground-600">
                  {topic.intro}
                </p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-accent-600">
                  包含 {topic.eventIds.length} 场赛事
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}